'use client'

import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'

/**
 * Explicit primary account chosen by account-scoped navigation for this card, independent
 * of whichever watchlist tab happens to be focused right now. Some flows that open the
 * option chain (e.g. clicking a row in unified-history-table.tsx) deliberately never
 * touch the chart's own watchlist tab for options — see
 * use-order-row-context-menu-actions.ts's "Do NOT mutate the chart tab" comment — so
 * resolving the viewing account purely from focusedTab.accountId can silently pick up
 * whatever account was pinned to an unrelated, previously-focused tab (or fall back to
 * aggregating every active account on the venue, if that unrelated tab's venue doesn't
 * even match). This atom is the authoritative "what account is this card showing right
 * now" value; focusedTab-based resolution (useCardAccountContext) is only a fallback
 * for when nothing has been explicitly picked yet.
 */
export const chartSelectedAccountIdAtomFamily = atomFamily((_cardId: string) =>
  atom<string | null>(null)
)

/**
 * An account a row click in a linked table (Positions, its order/history tabs) asks every chart
 * in its link group to show. The table's own card can't write the charts' picks directly — it
 * doesn't know which cards they are — so it posts here and each chart adopts it. `nonce` lets the
 * same account land again after the user moved the chart elsewhere. Keyed `${pageId}:${color}`.
 */
export const linkedChartAccountPickAtomFamily = atomFamily((_groupKey: string) =>
  atom<{ accountId: string; nonce: number } | null>(null)
)

export const linkedChartAccountGroupKey = (pageId: string, color: string) => `${pageId}:${color}`

/**
 * The option contract a linked chart's option chain is focused on (its tab names it, see
 * tab-grouped-chart.tsx's optionChainTabLabel), shared with its link group so the group's Order
 * Form can seed that contract (useOrderFormLinkedSeed). Null when the chain shows no focused
 * contract or the chart isn't in option mode. Written by the chart that owns it (`cardId`), so
 * another chart unmounting doesn't clear it. Keyed by linkedChartAccountGroupKey.
 */
export const linkedFocusedOptionContractAtomFamily = atomFamily((_groupKey: string) =>
  atom<{ cardId: string; exchange: number; symbol: string } | null>(null)
)
